import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "image", "loader", "counter", "prevBtn", "nextBtn"]
  static values = { items: Array }

  connect() {
    this.index = 0
    this.imageCache = new Map()
    this.abortController = new AbortController()
    this.boundKeydown = this.onKeydown.bind(this)
    this.boundDialogClose = this.onDialogClose.bind(this)
    this.boundBackdropClick = this.onBackdropClick.bind(this)
    if (this.hasDialogTarget) {
      this.dialogTarget.classList.add("hidden")
      this.dialogTarget.classList.remove("flex")
      this.dialogTarget.addEventListener("close", this.boundDialogClose)
      this.dialogTarget.addEventListener("click", this.boundBackdropClick)
    }
  }

  disconnect() {
    this.abortController.abort()
    window.removeEventListener("keydown", this.boundKeydown)
    if (this.hasDialogTarget) {
      this.dialogTarget.removeEventListener("close", this.boundDialogClose)
      this.dialogTarget.removeEventListener("click", this.boundBackdropClick)
      if (this.dialogTarget.open) {
        this.dialogTarget.close()
        document.body.classList.remove("overflow-hidden")
      }
    }
    this.imageCache.forEach(entry => {
      if (entry?.blobUrl?.startsWith("blob:")) URL.revokeObjectURL(entry.blobUrl)
    })
    this.imageCache.clear()
  }

  onDialogClose() {
    if (this.hasDialogTarget) {
      this.dialogTarget.classList.add("hidden")
      this.dialogTarget.classList.remove("flex")
    }
    document.body.classList.remove("overflow-hidden")
    window.removeEventListener("keydown", this.boundKeydown)
  }

  onBackdropClick(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.close()
      return
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.prev()
    }
    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    }
  }

  open(event) {
    event.preventDefault()
    const trigger = event.currentTarget.closest("[data-gallery-modal-open-index]")
    if (!trigger) return
    const idx = parseInt(trigger.dataset.galleryModalOpenIndex, 10)
    if (Number.isNaN(idx) || idx < 0 || idx >= this.itemsValue.length) return
    this.index = idx
    this.preloadAll()
    this.render()
    this.dialogTarget.classList.remove("hidden")
    this.dialogTarget.classList.add("flex")
    this.dialogTarget.showModal()
    document.body.classList.add("overflow-hidden")
    window.addEventListener("keydown", this.boundKeydown)
  }

  preloadAll() {
    const { signal } = this.abortController
    this.itemsValue.forEach(src => {
      if (this.imageCache.has(src)) return
      this.imageCache.set(src, { blobUrl: null, ready: false })

      fetch(src, { signal })
        .then(r => {
          if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`)
          return r.blob()
        })
        .then(blob => {
          if (signal.aborted) return
          const blobUrl = URL.createObjectURL(blob)
          this.imageCache.set(src, { blobUrl, ready: true })
          if (this.itemsValue[this.index] === src) this.displayCached(src)
        })
        .catch(err => {
          if (err.name === "AbortError") return
          // Network or HTTP failure — fall back to direct URL, still mark ready
          this.imageCache.set(src, { blobUrl: src, ready: true })
          if (this.itemsValue[this.index] === src) this.displayCached(src)
        })
    })
  }

  isCached(src) {
    const entry = this.imageCache.get(src)
    return entry?.ready === true
  }

  displayCached(src) {
    if (!this.hasImageTarget) return
    const { blobUrl } = this.imageCache.get(src)
    this.imageTarget.onload = () => {
      this.hideLoader()
      this.imageTarget.onload = null
    }
    this.imageTarget.onerror = () => {
      this.hideLoader()
      this.imageTarget.onerror = null
    }
    this.imageTarget.src = blobUrl
    this.imageTarget.alt = `Image ${this.index + 1} of ${this.itemsValue.length}`
  }

  close(event) {
    if (event) event.stopPropagation()
    if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close()
  }

  prev() {
    if (this.index <= 0) return
    this.index -= 1
    this.render()
  }

  next() {
    if (this.index >= this.itemsValue.length - 1) return
    this.index += 1
    this.render()
  }

  showLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hidden")
      this.loaderTarget.classList.add("flex")
    }
    if (this.hasImageTarget) {
      this.imageTarget.classList.add("invisible")
    }
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add("hidden")
      this.loaderTarget.classList.remove("flex")
    }
    if (this.hasImageTarget) {
      this.imageTarget.classList.remove("invisible")
    }
  }

  render() {
    const total = this.itemsValue.length
    const src = this.itemsValue[this.index]
    if (this.hasImageTarget) {
      if (this.isCached(src)) {
        this.showLoader()
        this.displayCached(src)
      } else {
        // preloadAll's fetch will call displayCached() when ready
        this.showLoader()
      }
    }
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = String(this.index + 1)
    }
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.disabled = this.index === 0
      this.prevBtnTarget.classList.toggle("opacity-40", this.index === 0)
      this.prevBtnTarget.classList.toggle("pointer-events-none", this.index === 0)
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.disabled = this.index >= total - 1
      this.nextBtnTarget.classList.toggle("opacity-40", this.index >= total - 1)
      this.nextBtnTarget.classList.toggle("pointer-events-none", this.index >= total - 1)
    }
  }
}
